// Proveedores Page - Complete CRUD with APPCC requirements
import React, { useEffect, useState } from 'react';
import { proveedoresAPI } from '../api/api';
import { Plus, Search, Edit, Trash2, FileText, CheckCircle2, AlertCircle, Clock, Building2, Phone, Mail, FileCheck, BarChart3 } from 'lucide-react';
import ProveedorEstadisticas from '../components/ProveedorEstadisticas';

const Proveedores = () => {
  const [proveedores, setProveedores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCalificacion, setFilterCalificacion] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [proveedorToDelete, setProveedorToDelete] = useState(null);
  const [ingredientesRelacionados, setIngredientesRelacionados] = useState([]);
  const [editingProveedor, setEditingProveedor] = useState(null);
  const [viewMode, setViewMode] = useState('listado'); // listado | estadisticas
  const [formData, setFormData] = useState({
    nombre: '',
    cif: '',
    persona_contacto: '',
    telefono: '',
    email: '',
    direccion: '',
    registro_sanitario: '',
    autorizacion_transporte: '',
    tipo_producto_servicio: '',
    calificacion: 'pendiente', // pendiente, validado, rechazado
    fecha_alta: new Date().toISOString().split('T')[0],
    notas: '',
    activo: true,
    categoria_defecto: '', // Categoría por defecto para cargos bancarios
    // Cómo se suministra (fecha de entrega propuesta al pedir)
    modo_suministro: 'pedido',
    dias_entrega: [],
    antelacion_dias: 1,
    entrega_mismo_dia: false,
    pedido_por_dia: false
  });

  // Días de la semana (0 = lunes) y insignias de suministro del listado
  const DIAS_SEMANA = ['lun', 'mar', 'mié', 'jue', 'vie', 'sáb', 'dom'];
  const insigniasSuministro = (p) => {
    const out = [];
    if (p.pedido_por_dia) out.push('Por día');
    if (p.modo_suministro === 'compra_directa') out.push('Compra directa');
    else if (Array.isArray(p.dias_entrega) && p.dias_entrega.length > 0) {
      out.push(p.dias_entrega.map(d => DIAS_SEMANA[d]).filter(Boolean).join(', '));
    }
    return out;
  };

  // Categorías de gasto disponibles
  const CATEGORIAS_GASTO = [
    { value: '', label: 'Sin categoría por defecto' },
    { value: 'alimentacion', label: 'Alimentación' },
    { value: 'alquileres', label: 'Alquileres' },
    { value: 'combustible', label: 'Combustible' },
    { value: 'comisiones_bancarias', label: 'Comisiones bancarias' },
    { value: 'compras', label: 'Compras' },
    { value: 'consumibles', label: 'Consumibles' },
    { value: 'extras', label: 'Extras' },
    { value: 'furgonetas', label: 'Furgonetas' },
    { value: 'gasolina', label: 'Gasolina' },
    { value: 'hacienda', label: 'Hacienda' },
    { value: 'impuestos', label: 'Impuestos' },
    { value: 'limpieza', label: 'Limpieza' },
    { value: 'mantenimiento', label: 'Mantenimiento' },
    { value: 'material', label: 'Material' },
    { value: 'nominas', label: 'Nóminas' },
    { value: 'otros', label: 'Otros' },
    { value: 'parking', label: 'Parking' },
    { value: 'seguros', label: 'Seguros' },
    { value: 'servicios', label: 'Servicios' },
    { value: 'suministros', label: 'Suministros' },
    { value: 'transporte', label: 'Transporte' },
  ];

  useEffect(() => {
    fetchProveedores();
  }, []);

  const fetchProveedores = async () => {
    try {
      const response = await proveedoresAPI.getAll();
      setProveedores(response.data);
    } catch (error) {
      console.error('Error fetching proveedores:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (proveedor) => {
    try {
      // Obtener ingredientes relacionados
      const response = await proveedoresAPI.getIngredientesRelacionados(proveedor.id);
      setIngredientesRelacionados(response.data.ingredientes || []);
      setProveedorToDelete(proveedor);
      setShowDeleteModal(true);
    } catch (error) {
      console.error('Error checking related ingredients:', error);
      alert('Error al verificar ingredientes relacionados');
    }
  };

  const confirmDelete = async () => {
    if (!proveedorToDelete) return;
    
    try {
      await proveedoresAPI.delete(proveedorToDelete.id);
      fetchProveedores();
      setShowDeleteModal(false);
      setProveedorToDelete(null);
      setIngredientesRelacionados([]);
    } catch (error) {
      console.error('Error deleting proveedor:', error);
      const errorMsg = error.response?.data?.detail || 'Error al eliminar el proveedor';
      alert(errorMsg);
    }
  };

  const openModal = (proveedor = null) => {
    if (proveedor) {
      setEditingProveedor(proveedor);
      setFormData({
        nombre: proveedor.nombre,
        cif: proveedor.cif || '',
        persona_contacto: proveedor.persona_contacto || '',
        telefono: proveedor.telefono || '',
        email: proveedor.email || '',
        direccion: proveedor.direccion || '',
        registro_sanitario: proveedor.registro_sanitario || '',
        autorizacion_transporte: proveedor.autorizacion_transporte || '',
        tipo_producto_servicio: proveedor.tipo_producto_servicio || '',
        calificacion: proveedor.calificacion || 'pendiente',
        fecha_alta: proveedor.fecha_alta || new Date().toISOString().split('T')[0],
        notas: proveedor.notas || '',
        activo: proveedor.activo !== undefined ? proveedor.activo : true,
        categoria_defecto: proveedor.categoria_defecto || '',
        modo_suministro: proveedor.modo_suministro || 'pedido',
        dias_entrega: Array.isArray(proveedor.dias_entrega) ? proveedor.dias_entrega : [],
        antelacion_dias: proveedor.antelacion_dias ?? 1,
        entrega_mismo_dia: !!proveedor.entrega_mismo_dia,
        pedido_por_dia: !!proveedor.pedido_por_dia
      });
    } else {
      setEditingProveedor(null);
      setFormData({
        nombre: '',
        cif: '',
        persona_contacto: '',
        telefono: '',
        email: '',
        direccion: '',
        registro_sanitario: '',
        autorizacion_transporte: '',
        tipo_producto_servicio: '',
        calificacion: 'pendiente',
        fecha_alta: new Date().toISOString().split('T')[0],
        notas: '',
        activo: true,
        categoria_defecto: '',
        modo_suministro: 'pedido',
        dias_entrega: [],
        antelacion_dias: 1,
        entrega_mismo_dia: false,
        pedido_por_dia: false
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingProveedor(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingProveedor) {
        await proveedoresAPI.update(editingProveedor.id, formData);
      } else {
        await proveedoresAPI.create(formData);
      }
      fetchProveedores();
      closeModal();
    } catch (error) {
      console.error('Error saving proveedor:', error);
      alert('Error al guardar el proveedor');
    }
  };

  const getCalificacionBadge = (calificacion) => {
    const badges = {
      validado: {
        bg: 'bg-green-100',
        text: 'text-green-800',
        icon: <CheckCircle2 className="w-4 h-4" />,
        label: 'Validado'
      },
      pendiente: {
        bg: 'bg-yellow-100',
        text: 'text-yellow-800',
        icon: <Clock className="w-4 h-4" />,
        label: 'Pendiente'
      },
      rechazado: {
        bg: 'bg-red-100',
        text: 'text-red-800',
        icon: <AlertCircle className="w-4 h-4" />,
        label: 'Rechazado'
      }
    };
    const badge = badges[calificacion] || badges.pendiente;
    return (
      <span className={`px-2 py-1 inline-flex items-center gap-1 text-xs font-semibold rounded-full ${badge.bg} ${badge.text}`}>
        {badge.icon}
        {badge.label}
      </span>
    );
  };

  const filteredProveedores = proveedores.filter(proveedor => {
    const matchesSearch = proveedor.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (proveedor.cif && proveedor.cif.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCalificacion = filterCalificacion === '' || proveedor.calificacion === filterCalificacion;
    return matchesSearch && matchesCalificacion;
  });

  if (loading) {
    return <div className="flex items-center justify-center h-64">Cargando...</div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Proveedores</h1>
          <p className="text-gray-600 mt-1">Gestión y control de proveedores según APPCC</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex bg-gray-100 rounded-lg p-0.5">
            <button
              onClick={() => setViewMode('listado')}
              data-testid="view-listado-btn"
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'listado'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <span className="hidden sm:inline">Listado</span>
              <Building2 className="w-4 h-4 sm:hidden" />
            </button>
            <button
              onClick={() => setViewMode('estadisticas')}
              data-testid="view-estadisticas-btn"
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'estadisticas'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <BarChart3 className="w-4 h-4 sm:hidden" />
              <span className="hidden sm:inline">Estadisticas</span>
            </button>
          </div>
          {viewMode === 'listado' && (
            <button
              onClick={() => openModal()}
              className="bg-duetto-primary text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-opacity-90 transition-colors"
            >
              <Plus className="w-5 h-5" />
              <span>Nuevo Proveedor</span>
            </button>
          )}
        </div>
      </div>

      {viewMode === 'estadisticas' && <ProveedorEstadisticas />}

      {viewMode === 'listado' && (<>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar por nombre o CIF..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
            />
          </div>
          <select
            value={filterCalificacion}
            onChange={(e) => setFilterCalificacion(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
          >
            <option value="">Todas las calificaciones</option>
            <option value="validado">Validado</option>
            <option value="pendiente">Pendiente</option>
            <option value="rechazado">Rechazado</option>
          </select>
        </div>
      </div>

      {/* Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredProveedores.map((proveedor) => (
          <div key={proveedor.id} className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow overflow-hidden">
            <div className="h-32 bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center">
              <Building2 className="w-16 h-16 text-white opacity-80" />
            </div>
            <div className="p-6">
              <div className="flex justify-between items-start mb-3">
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-gray-900 mb-1">{proveedor.nombre}</h3>
                  {proveedor.cif && (
                    <p className="text-sm text-gray-500">CIF: {proveedor.cif}</p>
                  )}
                </div>
                {getCalificacionBadge(proveedor.calificacion)}
              </div>

              {insigniasSuministro(proveedor).length > 0 && (
                <div className="mb-3 flex flex-wrap gap-1" data-testid={`proveedor-suministro-${proveedor.id}`}>
                  {insigniasSuministro(proveedor).map(texto => (
                    <span key={texto} className="px-2 py-0.5 text-xs font-semibold rounded-full bg-[#E3F3EC] text-[#0F5E43]">{texto}</span>
                  ))}
                </div>
              )}

              {proveedor.tipo_producto_servicio && (
                <div className="mb-3">
                  <p className="text-xs text-gray-500 mb-1">Suministra:</p>
                  <p className="text-sm text-gray-700">{proveedor.tipo_producto_servicio}</p>
                </div>
              )}

              {proveedor.categoria_defecto && (
                <div className="mb-3">
                  <p className="text-xs text-gray-500 mb-1">Categoría gastos:</p>
                  <span className="inline-block px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                    {CATEGORIAS_GASTO.find(c => c.value === proveedor.categoria_defecto)?.label || proveedor.categoria_defecto}
                  </span>
                </div>
              )}

              <div className="space-y-2 mb-4">
                {proveedor.persona_contacto && (
                  <div className="flex items-center text-sm text-gray-600">
                    <Phone className="w-4 h-4 mr-2" />
                    <span>{proveedor.persona_contacto}</span>
                  </div>
                )}
                {proveedor.telefono && (
                  <div className="flex items-center text-sm text-gray-600">
                    <Phone className="w-4 h-4 mr-2" />
                    <span>{proveedor.telefono}</span>
                  </div>
                )}
                {proveedor.email && (
                  <div className="flex items-center text-sm text-gray-600">
                    <Mail className="w-4 h-4 mr-2" />
                    <span className="truncate">{proveedor.email}</span>
                  </div>
                )}
                {proveedor.registro_sanitario && (
                  <div className="flex items-center text-sm text-gray-600">
                    <FileCheck className="w-4 h-4 mr-2 text-green-600" />
                    <span className="truncate">RS: {proveedor.registro_sanitario}</span>
                  </div>
                )}
              </div>

              <div className="flex space-x-2 pt-3 border-t border-gray-200">
                <button 
                  onClick={() => openModal(proveedor)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center justify-center space-x-1"
                >
                  <Edit className="w-4 h-4" />
                  <span>Editar</span>
                </button>
                <button
                  onClick={() => handleDelete(proveedor)}
                  className="px-3 py-2 border border-red-200 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 flex items-center justify-center"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredProveedores.length === 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No se encontraron proveedores</p>
        </div>
      )}
      </>)}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">
                {editingProveedor ? 'Editar Proveedor' : 'Nuevo Proveedor'}
              </h2>
              <button onClick={closeModal} className="text-gray-500 hover:text-gray-700">
                <span className="text-2xl">×</span>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6">
              {/* Datos Básicos */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Building2 className="w-5 h-5 mr-2 text-duetto-primary" />
                  Datos Básicos
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nombre / Razón Social *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.nombre}
                      onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
                      placeholder="Nombre completo del proveedor"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      CIF *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.cif}
                      onChange={(e) => setFormData({ ...formData, cif: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
                      placeholder="A12345678"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Fecha de Alta
                    </label>
                    <input
                      type="date"
                      value={formData.fecha_alta}
                      onChange={(e) => setFormData({ ...formData, fecha_alta: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tipo de Producto/Servicio que Suministra
                    </label>
                    <input
                      type="text"
                      value={formData.tipo_producto_servicio}
                      onChange={(e) => setFormData({ ...formData, tipo_producto_servicio: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
                      placeholder="Ej: Frutas y verduras frescas, Carnes, Pescados, etc."
                    />
                  </div>
                </div>
              </div>

              {/* Contacto */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Phone className="w-5 h-5 mr-2 text-duetto-primary" />
                  Información de Contacto
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Persona de Contacto
                    </label>
                    <input
                      type="text"
                      value={formData.persona_contacto}
                      onChange={(e) => setFormData({ ...formData, persona_contacto: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
                      placeholder="Nombre del contacto principal"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Teléfono
                    </label>
                    <input
                      type="tel"
                      value={formData.telefono}
                      onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
                      placeholder="+34 XXX XXX XXX"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
                      placeholder="contacto@proveedor.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Dirección
                    </label>
                    <input
                      type="text"
                      value={formData.direccion}
                      onChange={(e) => setFormData({ ...formData, direccion: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
                      placeholder="Calle, número, ciudad, CP"
                    />
                  </div>
                </div>
              </div>

              {/* Certificaciones y Registros (APPCC) */}
              <div className="mb-6 p-4 bg-green-50 rounded-lg border border-green-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <FileCheck className="w-5 h-5 mr-2 text-green-600" />
                  Certificaciones y Registros (APPCC)
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nº Registro Sanitario *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.registro_sanitario}
                      onChange={(e) => setFormData({ ...formData, registro_sanitario: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
                      placeholder="RS-XX.XXXXXX/X"
                    />
                    <p className="text-xs text-gray-500 mt-1">Obligatorio según APPCC</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Autorización de Transporte
                    </label>
                    <input
                      type="text"
                      value={formData.autorizacion_transporte}
                      onChange={(e) => setFormData({ ...formData, autorizacion_transporte: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
                      placeholder="AT-XX.XXXXXX"
                    />
                    <p className="text-xs text-gray-500 mt-1">Si aplica</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Calificación Actual *
                    </label>
                    <select
                      required
                      value={formData.calificacion}
                      onChange={(e) => setFormData({ ...formData, calificacion: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
                    >
                      <option value="pendiente">Pendiente de Validación</option>
                      <option value="validado">Validado</option>
                      <option value="rechazado">Rechazado</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">Estado de homologación</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Categoría por Defecto (Gastos)
                    </label>
                    <select
                      value={formData.categoria_defecto || ''}
                      onChange={(e) => setFormData({ ...formData, categoria_defecto: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
                    >
                      {CATEGORIAS_GASTO.map(cat => (
                        <option key={cat.value} value={cat.value}>{cat.label}</option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">Se asignará automáticamente a los cargos bancarios</p>
                  </div>
                </div>
              </div>

              {/* Suministro: cómo se pide y cuándo entrega */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200" data-testid="proveedor-suministro">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">Suministro</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Cómo se suministra</label>
                    <select
                      value={formData.modo_suministro || 'pedido'}
                      onChange={(e) => setFormData({ ...formData, modo_suministro: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
                      data-testid="proveedor-modo-suministro"
                    >
                      <option value="pedido">Se pide (lo trae o se recoge)</option>
                      <option value="compra_directa">Vamos a comprar (Mercadona, Carrefour...)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Antelación (días)</label>
                    <input
                      type="number"
                      min="0"
                      max="30"
                      step="1"
                      value={formData.antelacion_dias ?? 1}
                      onChange={(e) => setFormData({ ...formData, antelacion_dias: e.target.value === '' ? 0 : Math.max(0, Math.round(Number(e.target.value) || 0)) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
                      data-testid="proveedor-antelacion"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {formData.modo_suministro === 'compra_directa'
                        ? 'Días antes del evento en que se va a comprar (1 = la víspera)'
                        : 'Días mínimos entre el pedido y la entrega'}
                    </p>
                  </div>
                </div>
                {formData.modo_suministro !== 'compra_directa' && (
                  <div className="mt-4">
                    <span className="block text-sm font-medium text-gray-700 mb-1">Días en que entrega</span>
                    <div className="flex flex-wrap gap-2">
                      {DIAS_SEMANA.map((dia, i) => (
                        <label key={dia} className="flex items-center gap-1 text-sm text-gray-700">
                          <input
                            type="checkbox"
                            checked={(formData.dias_entrega || []).includes(i)}
                            onChange={(e) => {
                              const actuales = new Set(formData.dias_entrega || []);
                              if (e.target.checked) actuales.add(i);
                              else actuales.delete(i);
                              setFormData({ ...formData, dias_entrega: Array.from(actuales).sort((a, b) => a - b) });
                            }}
                            className="rounded border-gray-300 text-duetto-primary focus:ring-duetto-primary"
                            data-testid={`proveedor-dia-${i}`}
                          />
                          {dia}
                        </label>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Sin marcar ninguno: cualquier día. Se propone la víspera del evento (o el último día de entrega antes).</p>
                    <label className="mt-2 flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={!!formData.entrega_mismo_dia}
                        onChange={(e) => setFormData({ ...formData, entrega_mismo_dia: e.target.checked })}
                        className="rounded border-gray-300 text-duetto-primary focus:ring-duetto-primary"
                      />
                      Puede entregar el mismo día del evento
                    </label>
                  </div>
                )}
                <label className="mt-4 flex items-start gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={!!formData.pedido_por_dia}
                    onChange={(e) => setFormData({ ...formData, pedido_por_dia: e.target.checked })}
                    className="mt-0.5 rounded border-gray-300 text-duetto-primary focus:ring-duetto-primary"
                    data-testid="proveedor-pedido-por-dia"
                  />
                  <span>
                    <span className="font-semibold text-gray-900">Pedido por día de entrega</span>
                    <span className="block text-xs text-gray-500">El pedido a este proveedor se desglosa por día: una cantidad por cada día de evento</span>
                  </span>
                </label>
              </div>

              {/* Notas */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notas / Observaciones
                </label>
                <textarea
                  rows="3"
                  value={formData.notas}
                  onChange={(e) => setFormData({ ...formData, notas: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
                  placeholder="Información adicional sobre el proveedor..."
                />
              </div>

              {/* Estado */}
              <div className="mb-6">
                <label className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors">
                  <input
                    type="checkbox"
                    checked={formData.activo}
                    onChange={(e) => setFormData({ ...formData, activo: e.target.checked })}
                    className="w-5 h-5 rounded border-gray-300 text-duetto-primary focus:ring-duetto-primary"
                  />
                  <div>
                    <span className="text-sm font-semibold text-gray-900">Proveedor activo</span>
                    <p className="text-xs text-gray-500">Los proveedores inactivos no aparecerán en los listados</p>
                  </div>
                </label>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-duetto-primary text-white rounded-lg hover:bg-opacity-90"
                >
                  {editingProveedor ? 'Actualizar Proveedor' : 'Crear Proveedor'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <AlertCircle className="w-6 h-6 text-red-600 mr-3" />
                <h3 className="text-lg font-semibold text-gray-900">
                  Confirmar Eliminación
                </h3>
              </div>
              
              <p className="text-gray-600 mb-4">
                ¿Estás seguro de eliminar el proveedor <strong>{proveedorToDelete?.nombre}</strong>?
              </p>

              {ingredientesRelacionados.length > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                  <div className="flex items-start">
                    <AlertCircle className="w-5 h-5 text-yellow-600 mr-2 mt-0.5" />
                    <div>
                      <h4 className="text-sm font-semibold text-yellow-800 mb-2">
                        Ingredientes relacionados:
                      </h4>
                      <ul className="text-sm text-yellow-700 space-y-1">
                        {ingredientesRelacionados.map((ingrediente, index) => (
                          <li key={index}>• {ingrediente.nombre}</li>
                        ))}
                      </ul>
                      <p className="text-xs text-yellow-600 mt-2">
                        Estos ingredientes quedarán sin proveedor asignado.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setProveedorToDelete(null);
                    setIngredientesRelacionados([]);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={confirmDelete}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Eliminar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Proveedores;
